import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Divide, Eraser, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import { api, errMsg, fmtDateTime } from "../../lib/api";

/**
 * Phase 214 — Monthly Collection Targets editor (₹ per month for one Financial Year).
 * Used inline on Admin → Settings and inside the "Set targets" dialog of the Monthly Collection graph.
 * Saved targets are drawn as the dashed target line on the graph; blank / 0 clears a month.
 */
const money = (n) => "\u20B9" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const compact = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `\u20B9${(v / 1e7).toFixed(v >= 1e8 ? 1 : 2)} Cr`;
  if (v >= 1e5) return `\u20B9${(v / 1e5).toFixed(v >= 1e6 ? 1 : 2)} L`;
  return money(v);
};
const parseAmt = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).replace(/[,\s\u20B9]/g, "");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

export function CollectionTargetsEditor({ code, fyId: initialFyId = "", readOnly = false, onSaved, onLoaded, onFyChange, testIdPrefix = "collection-targets", showFySelect = true, className = "" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fyId, setFyId] = useState(initialFyId || "");
  const [form, setForm] = useState({});          // { "4": "150000", ... } strings as typed
  const [annual, setAnnual] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (id) => {
    setError(null);
    try {
      const r = await api.get(`/t/${code}/settings/collection-targets`, { params: id ? { fy_id: id } : {} });
      setData(r.data);
      setForm(Object.fromEntries(r.data.months.map((m) => [String(m.month), m.target != null ? String(m.target) : ""])));
      if (!id && r.data?.fy?.id) setFyId(r.data.fy.id);
      onLoaded?.(r.data);
    } catch (e) { setError(errMsg(e)); }
  }, [code]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setData(null); load(fyId); }, [load, fyId]);
  useEffect(() => { if (initialFyId && initialFyId !== fyId) setFyId(initialFyId); }, [initialFyId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const months = data?.months || [];
  const parsed = useMemo(() => Object.fromEntries(months.map((m) => [String(m.month), parseAmt(form[String(m.month)])])), [months, form]);
  const invalidMonths = months.filter((m) => { const v = parsed[String(m.month)]; return v !== null && (Number.isNaN(v) || v < 0 || v > (data?.max_target || 1e12)); });
  const total = months.reduce((s, m) => { const v = parsed[String(m.month)]; return s + (v && v > 0 && Number.isFinite(v) ? v : 0); }, 0);
  const setCount = months.filter((m) => { const v = parsed[String(m.month)]; return v && v > 0 && Number.isFinite(v); }).length;
  const dirty = months.some((m) => { const v = parsed[String(m.month)]; const cur = v && v > 0 && Number.isFinite(v) ? v : null; return cur !== (m.target ?? null); });

  const splitEvenly = () => {
    const a = parseAmt(annual);
    if (!a || a <= 0 || Number.isNaN(a)) return toast.error("Enter the annual target amount first");
    const base = Math.floor((a * 100) / 12) / 100;
    const remainder = Math.round((a - base * 12) * 100) / 100;
    const next = {};
    months.forEach((m, i) => { next[String(m.month)] = String(Math.round((base + (i === months.length - 1 ? remainder : 0)) * 100) / 100); });
    setForm(next);
    toast.info(`Annual target ${money(a)} split evenly — ${money(base)} per month${remainder ? ` (${money(remainder)} rounding added to ${months[months.length - 1]?.label})` : ""}`);
  };
  const clearAll = () => setForm(Object.fromEntries(months.map((m) => [String(m.month), ""])));

  const save = async () => {
    if (invalidMonths.length) return toast.error(`Fix the highlighted month${invalidMonths.length === 1 ? "" : "s"} — each target must be a positive number`);
    setBusy(true);
    try {
      const targets = Object.fromEntries(months.map((m) => { const v = parsed[String(m.month)]; return [String(m.month), v && v > 0 ? v : null]; }));
      const r = await api.put(`/t/${code}/settings/collection-targets`, { fy_id: data?.fy?.id || fyId, targets });
      setData(r.data);
      setForm(Object.fromEntries(r.data.months.map((m) => [String(m.month), m.target != null ? String(m.target) : ""])));
      toast.success(`Targets saved for FY ${r.data.fy?.fy_label}`, { description: r.data.has_targets ? `Annual target ${money(r.data.annual_total)} across ${r.data.months_set} month${r.data.months_set === 1 ? "" : "s"}. The graph's target line updates right away.` : "All targets cleared — the target line is removed from the graph." });
      onSaved?.(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (error && !data) {
    return (
      <div className={`text-sm text-destructive flex items-center justify-between gap-2 ${className}`} data-testid={`${testIdPrefix}-error`}>
        <span>{error}</span>
        <Button size="sm" variant="outline" onClick={() => load(fyId)} data-testid={`${testIdPrefix}-retry`}>Try again</Button>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`} data-testid={`${testIdPrefix}-editor`}>
      <div className="flex flex-wrap items-end gap-3">
        {showFySelect && (
          <div className="space-y-1">
            <Label className="text-xs" htmlFor={`${testIdPrefix}-fy-select`}>Financial year</Label>
            <Select value={fyId || data?.fy?.id || ""} onValueChange={(v) => { setFyId(v); onFyChange?.(v); }} disabled={!data?.financial_years?.length}>
              <SelectTrigger id={`${testIdPrefix}-fy-select`} className="h-9 w-[210px] text-sm" data-testid={`${testIdPrefix}-fy-select`} aria-label="Financial year">
                <SelectValue placeholder="Financial year" />
              </SelectTrigger>
              <SelectContent>
                {(data?.financial_years || []).map((f) => (
                  <SelectItem key={f.id} value={f.id} data-testid={`${testIdPrefix}-fy-option-${f.fy_label}`}>FY {f.fy_label}{f.virtual ? " · not in master" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {!readOnly && (
          <div className="space-y-1">
            <Label className="text-xs" htmlFor={`${testIdPrefix}-annual-input`}>Annual target (₹) — quick fill</Label>
            <div className="flex items-center gap-2">
              <Input id={`${testIdPrefix}-annual-input`} inputMode="decimal" placeholder="e.g. 36000000" value={annual} onChange={(e) => setAnnual(e.target.value)}
                className="h-9 w-40 tabular-nums" data-testid={`${testIdPrefix}-annual-input`} disabled={!data} />
              <Button type="button" variant="secondary" size="sm" className="h-9" onClick={splitEvenly} disabled={!data || !annual} data-testid={`${testIdPrefix}-split-button`}>
                <Divide className="h-3.5 w-3.5 mr-1.5" /> Split evenly over 12 months
              </Button>
            </div>
          </div>
        )}
      </div>

      {!data ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid={`${testIdPrefix}-loading`}>
          {Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
        </div>
      ) : (
        <fieldset disabled={readOnly || busy} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 min-w-0" data-testid={`${testIdPrefix}-months`}>
          {months.map((m) => {
            const k = String(m.month);
            const v = parsed[k];
            const bad = v !== null && (Number.isNaN(v) || v < 0 || v > (data.max_target || 1e12));
            return (
              <div key={m.key} className={`rounded-md border p-2 space-y-1 ${bad ? "border-red-400 bg-red-50/40" : "bg-background"}`} data-testid={`${testIdPrefix}-month-${m.month}`}>
                <Label htmlFor={`${testIdPrefix}-input-${m.month}`} className="text-[11px] text-muted-foreground flex items-center justify-between">
                  <span>{m.label} {m.year}</span>
                  {v > 0 && !bad ? <span className="tabular-nums text-foreground/70" data-testid={`${testIdPrefix}-compact-${m.month}`}>{compact(v)}</span> : null}
                </Label>
                <Input id={`${testIdPrefix}-input-${m.month}`} inputMode="decimal" placeholder="—" value={form[k] ?? ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-8 text-sm tabular-nums" aria-invalid={bad ? "true" : undefined}
                  data-testid={`${testIdPrefix}-input-${m.month}`} />
              </div>
            );
          })}
        </fieldset>
      )}

      {data && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs" data-testid={`${testIdPrefix}-summary`}>
          <span className="tabular-nums">
            Annual target: <b data-testid={`${testIdPrefix}-total`}>{money(total)}</b> <span className="text-muted-foreground">· {setCount} of 12 months set{data.updated_at ? ` · last saved ${fmtDateTime(data.updated_at)}${data.updated_by_name ? ` by ${data.updated_by_name}` : ""}` : ""}</span>
          </span>
          {invalidMonths.length > 0 && <span className="text-red-700 font-medium flex items-center gap-1" data-testid={`${testIdPrefix}-invalid-note`}><AlertTriangle className="h-3.5 w-3.5" /> {invalidMonths.length} month{invalidMonths.length === 1 ? "" : "s"} need a valid positive amount</span>}
        </div>
      )}

      {!readOnly && data && (
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={save} disabled={busy || !dirty || invalidMonths.length > 0} data-testid={`${testIdPrefix}-save`}>
            <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save targets"}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={clearAll} disabled={busy || setCount === 0} data-testid={`${testIdPrefix}-clear`}>
            <Eraser className="h-3.5 w-3.5 mr-1.5" /> Clear all
          </Button>
          {dirty && invalidMonths.length === 0 && <span className="text-[11px] text-amber-800" data-testid={`${testIdPrefix}-dirty-note`}>Unsaved changes</span>}
        </div>
      )}
    </div>
  );
}
